// Core domain types for Klondike Solitaire.

export type Suit = "S" | "H" | "D" | "C"; // spades, hearts, diamonds, clubs
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
export type Color = "red" | "black";

export interface Card {
  readonly id: number; // stable 0..51 within a deal
  readonly suit: Suit;
  readonly rank: Rank;
  faceUp: boolean;
  readonly isJoker?: boolean;
}

export const colorOf = (suit: Suit): Color =>
  suit === "H" || suit === "D" ? "red" : "black";

// Klondike has 7 tableau piles, 4 foundation piles, 1 stock, 1 waste.
export type PileKind = "tableau" | "foundation" | "stock" | "waste";

export interface PileId {
  kind: PileKind;
  index: number; // 0..6 for tableau, 0..3 for foundation; 0 for stock/waste
}

export type DrawMode = "one" | "three";
export type ScoringSystem = "windows-standard";

export interface GameState {
  tableau: Card[][]; // 7 piles, bottom-first
  foundation: Card[][]; // 4 piles, bottom-first
  stock: Card[]; // top of pile = end of array
  waste: Card[]; // top of pile = end of array
  drawMode: DrawMode;
  stockRecycles: number;
  scoringSystem: ScoringSystem;
  score: number;
  moves: number;
  startedAt: number; // ms epoch
  elapsedMs: number; // accumulated play time (paused-aware later)
  undoStack: Snapshot[];
  hintsUsed: number;
  undosUsed: number;
  finished: boolean;
  won: boolean;
  seed: string; // deal identifier for reproducibility
  joker: JokerGameState;
}

export interface JokerGameState {
  usedThisGame: boolean;
  pendingSpend: boolean;
}

// Lightweight snapshot for undo. Keep small — store only mutable parts.
export interface Snapshot {
  tableau: Card[][];
  foundation: Card[][];
  stock: Card[];
  waste: Card[];
  stockRecycles: number;
  score: number;
  moves: number;
  hintsUsed: number;
  undosUsed: number;
  joker: JokerGameState;
}

export interface Move {
  from: PileId;
  to: PileId;
  count: number; // number of cards moved (>1 only for tableau→tableau)
}

export const TABLEAU_COUNT = 7;
export const FOUNDATION_COUNT = 4;

export const cardEquals = (a: Card, b: Card): boolean => a.id === b.id;

export const topOf = <T>(pile: T[]): T | undefined => pile[pile.length - 1];
